import type { ConverterMode, ConverterSerial, NetworkDoc, NetworkLink, NetworkNode, NetworkNodeKind, NetworkSegment, NetworkSide } from '../../../types'

// 현장 네트워크 도면 — 노드 종류, IP·대역 계산, IP 표 분석.
// 도면에는 IP 를 가진 실물(Edge 서버·스위치·공유기·변환기·설비·PC)만 그린다.
// LiveEdge 디바이스는 장비가 아니라 통신 역할이라 노드로 안 그리고, 접속 IP 가 같은 장비 블럭에 ● 코드로 붙인다.
// 시리얼로만 붙는 설비는 IP 가 없으니 안 그리고 변환기 블럭의 방식·메모로 적는다.

export const newKey = () => crypto.randomUUID().replace(/-/g, '')

export const KINDS: { kind: NetworkNodeKind; label: string; short: string; ports?: number }[] = [
  { kind: 'edge', label: 'Edge 서버', short: 'EDGE' },
  { kind: 'switch', label: '스위치', short: 'SW', ports: 8 },
  { kind: 'hub', label: '허브', short: 'HUB', ports: 5 },
  { kind: 'router', label: '라우터·공유기', short: 'RT' },
  { kind: 'converter', label: '변환기 (Serial↔TCP)', short: 'CONV' },
  { kind: 'equipment', label: '설비 (PLC 등)', short: 'EQ' },
  { kind: 'pc', label: 'PC·HMI', short: 'PC' },
  { kind: 'device', label: 'Edge 디바이스', short: 'DEV' },
  { kind: 'other', label: '기타', short: 'ETC' }, // 추가 목록엔 없음 — 예전 도면(설비·PLC)·모르는 종류 표시용
]
export const kindInfo = (kind: NetworkNodeKind) => KINDS.find(k => k.kind === kind) ?? KINDS[KINDS.length - 1]

/** 툴바 [추가] 에서 고를 수 있는 종류 — Edge 서버는 하나뿐이라 자동, 디바이스·기타는 예전 도면 호환용 */
export const ADDABLE = KINDS.filter(k => k.kind !== 'edge' && k.kind !== 'device' && k.kind !== 'other')

export function newNode(kind: NetworkNodeKind, x: number, y: number, patch: Partial<NetworkNode> = {}): NetworkNode {
  return {
    key: newKey(), kind, name: '', ip: '', mac: '', model: '', location: '', memo: '', deviceCode: '',
    ports: kindInfo(kind).ports ?? 0, x, y, serial: '', mode: '', ...patch,
  }
}

// 변환기 방식 — 시리얼 쪽(설비와 이은 선)과 네트워크 쪽(Edge 가 붙는 방식)
export const SERIALS: ConverterSerial[] = ['RS-485', 'RS-422', 'RS-232']
export const MODES: { mode: ConverterMode; label: string; hint: string }[] = [
  { mode: 'tcp-server', label: 'TCP Server', hint: 'Edge → 변환기 IP:포트로 접속' },
  { mode: 'tcp-client', label: 'TCP Client', hint: '변환기 → Edge로 접속' },
  { mode: 'real-com', label: 'Real COM', hint: 'Edge PC에서 가상 COM 포트로 사용' },
  { mode: 'udp', label: 'UDP', hint: '' },
]
/** 변환기 블럭 한 줄 — "RS-485 → TCP Server" */
export function convLine(n: NetworkNode): string {
  if (n.kind !== 'converter' || (!n.serial && !n.mode)) return ''
  return `${n.serial || 'Serial'} → ${MODES.find(m => m.mode === n.mode)?.label ?? 'TCP'}`
}

/** 다른 노드와 겹치지 않는 자리 — 겹치면 아래로 내린다 (노드 크기 약 190×85) */
export function freeSpot(nodes: NetworkNode[], x: number, y: number): { x: number; y: number } {
  let py = y
  for (let i = 0; i < 50 && nodes.some(n => Math.abs(n.x - x) < 200 && Math.abs(n.y - py) < 95); i++) py += 105
  return { x, y: py }
}

export const newSegment = (): NetworkSegment => ({ key: newKey(), name: '', cidr: '', vlan: '', gateway: '', memo: '' })
export const newLink = (from: string, to: string, fromSide: NetworkSide | '' = '', toSide: NetworkSide | '' = ''): NetworkLink =>
  ({ key: newKey(), from, fromPort: '', to, toPort: '', label: '', fromSide, toSide })

/** 노드 연결점 — 위·아래·왼쪽·오른쪽·네 모서리 */
export const SIDES: NetworkSide[] = ['t', 'b', 'l', 'r', 'tl', 'tr', 'bl', 'br']
export const asSide = (v: string | null | undefined, fallback: NetworkSide): NetworkSide =>
  SIDES.includes(v as NetworkSide) ? (v as NetworkSide) : fallback

// ── IP ──

export function ipToInt(ip: string): number | null {
  const m = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.exec(ip.trim())
  if (!m) return null
  const parts = m.slice(1).map(Number)
  if (parts.some(p => p > 255)) return null
  return ((parts[0] << 24) >>> 0) + (parts[1] << 16) + (parts[2] << 8) + parts[3]
}

export const intToIp = (n: number) => [n >>> 24, (n >>> 16) & 255, (n >>> 8) & 255, n & 255].join('.')

export function parseCidr(cidr: string): { base: number; bits: number } | null {
  const m = /^(.+)\/(\d{1,2})$/.exec(cidr.trim())
  if (!m) return null
  const ip = ipToInt(m[1])
  const bits = Number(m[2])
  if (ip == null || bits > 32) return null
  const mask = bits === 0 ? 0 : (0xffffffff << (32 - bits)) >>> 0
  return { base: (ip & mask) >>> 0, bits }
}

export function inCidr(ip: string, cidr: string): boolean {
  const n = ipToInt(ip), c = parseCidr(cidr)
  if (n == null || !c) return false
  const mask = c.bits === 0 ? 0 : (0xffffffff << (32 - c.bits)) >>> 0
  return ((n & mask) >>> 0) === c.base
}

/** 채널 접속 값에서 IP 만 — "192.168.30.101:4001", "opc.tcp://10.0.0.5:4840/…" */
export function ipFrom(text: string | undefined | null): string {
  const m = /(\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3})/.exec(text ?? '')
  return m && ipToInt(m[1]) != null ? m[1] : ''
}

// ── Edge 디바이스 실시간 정보 ──

export type LiveDevice = { code: string; name: string; protocol: string; ip: string; port: string; state: 'ok' | 'fault' | 'unknown' }

/** 노드의 표시 IP·이름 — 디바이스는 Edge 값 우선 */
export function nodeView(n: NetworkNode, live: Map<string, LiveDevice>) {
  if (n.kind !== 'device') return { name: n.name, ip: n.ip, live: undefined as LiveDevice | undefined, missing: false }
  const d = live.get(n.deviceCode)
  return { name: d?.name || n.name || n.deviceCode, ip: d?.ip || n.ip, live: d, missing: live.size > 0 && !d }
}

export const lampOf = (d: LiveDevice) => (d.state === 'ok' ? 'run' : d.state === 'fault' ? 'fault' : 'off') as 'run' | 'fault' | 'off'

/** 이 장비와 통신하는 Edge 디바이스 — 접속 IP 가 장비 IP 와 같은 것. Edge 서버 노드는 자기 PC(127.x)로 붙는 것도 */
export function commsOf(n: NetworkNode, live: Map<string, LiveDevice>): LiveDevice[] {
  if (n.kind === 'device') return []
  const ip = n.ip.trim()
  return [...live.values()].filter(d => d.ip && (d.ip === ip || (n.kind === 'edge' && d.ip.startsWith('127.'))))
}

/** 접속 IP 의 장비가 도면에 없는 Edge 디바이스 — IP 별로 묶어서. IP 가 없는 디바이스(DB·내부 모듈 등)는 noIp */
export function orphanComms(doc: NetworkDoc, live: Map<string, LiveDevice>) {
  const owned = new Set<string>()
  for (const n of doc.nodes) for (const d of commsOf(n, live)) owned.add(d.code)
  const byIp = new Map<string, LiveDevice[]>()
  const noIp: LiveDevice[] = []
  for (const d of live.values()) {
    if (owned.has(d.code)) continue
    if (!d.ip) noIp.push(d)
    else byIp.set(d.ip, [...(byIp.get(d.ip) ?? []), d])
  }
  const ips = [...byIp.entries()].map(([ip, devices]) => ({ ip, devices }))
    .sort((a, b) => (ipToInt(a.ip) ?? Infinity) - (ipToInt(b.ip) ?? Infinity))
  return { ips, noIp }
}

// ── IP 표 ──

export type IpRow = {
  ip: string
  nodes: NetworkNode[]
  devices: LiveDevice[] // 이 IP 로 통신하는 Edge 디바이스
  segment: NetworkSegment | null
  issue: string | null
}

/**
 * IP 별로 묶은 표. 시리얼 변환기 뒤의 디바이스(변환기와 같은 IP)는 겹쳐도 정상 — RS-485 로 한 IP 를 나눠 쓴다.
 * 그 밖에 한 IP 를 두 노드가 쓰면 중복, 대역이 있는데 어느 대역에도 안 들면 대역 밖.
 */
export function ipTable(doc: NetworkDoc, live: Map<string, LiveDevice>): IpRow[] {
  const byIp = new Map<string, NetworkNode[]>()
  for (const n of doc.nodes) {
    const ip = nodeView(n, live).ip
    if (!ip) continue
    byIp.set(ip, [...(byIp.get(ip) ?? []), n])
  }
  const rows: IpRow[] = [...byIp.entries()].map(([ip, nodes]) => {
    const segment = doc.segments.find(s => inCidr(ip, s.cidr)) ?? null
    const hosts = nodes.filter(n => n.kind !== 'device')
    const deviceNodes = nodes.filter(n => n.kind === 'device')
    // 디바이스끼리 같은 IP 는 정상 (변환기·게이트웨이 하나에 슬레이브 여럿) — 장비끼리, 또는 변환기 아닌 장비와 디바이스가 겹칠 때만
    const issue = ipToInt(ip) == null ? 'IP 형식 오류'
      : hosts.length > 1 ? `IP 중복 (${hosts.length}대)`
        : hosts.length === 1 && deviceNodes.length > 0 && hosts[0].kind !== 'converter' ? 'IP 중복 (디바이스와 장비)'
          : doc.segments.some(s => parseCidr(s.cidr)) && !segment ? '대역 밖'
            : null
    const devices = [...live.values()].filter(d => d.ip === ip)
    return { ip, nodes, devices, segment, issue }
  })
  return rows.sort((a, b) => (ipToInt(a.ip) ?? Infinity) - (ipToInt(b.ip) ?? Infinity))
}

/** 대역 안에서 아직 안 쓴 IP 몇 개 (게이트웨이·네트워크·브로드캐스트 제외) */
export function freeIps(segment: NetworkSegment, used: Set<string>, limit = 5): string[] {
  const c = parseCidr(segment.cidr)
  if (!c || c.bits < 16 || c.bits > 30) return []
  const size = 2 ** (32 - c.bits)
  const out: string[] = []
  for (let i = 1; i < size - 1 && out.length < limit; i++) {
    const ip = intToIp(c.base + i)
    if (!used.has(ip) && ip !== segment.gateway.trim()) out.push(ip)
  }
  return out
}
